/* Text-Flare runtime — scroll-triggered text effects.
   Hooks: data-te-effect, data-te-duration, data-te-delay, data-te-stagger,
          data-te-words (on the heading/text block wrapper).
   Behavior:
     - On reveal (IntersectionObserver), the chosen effect runs once. Split
       effects (word/char/fade/blur) break the text into unit spans and
       stagger them in; continuous effects (gradient/shiny) toggle a CSS class;
       text-rewriters (typewriter/count-up/rotating/scramble) animate content.
     - Respects prefers-reduced-motion: the original (final) text is left
       untouched — no split, no animation.
     - Robust: bails cleanly when the target has no text. */

(function () {
  /* Shared motion guard — re-evaluated per init/boot (not cached once) so a
     merchant toggling reveal-on-scroll or an OS-level change is honored by
     later inits. Prefers the canonical helper from layout/theme.liquid. */
  function motionOff() {
    if (window.nrdMotionOff) return window.nrdMotionOff();
    if (window.NerdioMotion && window.NerdioMotion.reduced) return window.NerdioMotion.reduced();
    return (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      document.documentElement.dataset.motionEnabled === 'false';
  }

  function cfgStagger() {
    return (window.NerdioMotionConfig && window.NerdioMotionConfig.stagger) || 40;
  }

  function num(el, attr, dflt) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? dflt : v;
  }

  /* The element that actually holds the text. data-te-* lives on the block
     wrapper (matching where animation-attrs lives); the heading/text element
     is a child. Fall back to the wrapper itself for safety. */
  function resolveTarget(wrap) {
    return wrap.querySelector('.heading, .text-block__content') || wrap;
  }

  function observe(wrap, cb) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { io.unobserve(e.target); cb(); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    io.observe(wrap);
  }

  /* Split a target's text nodes into unit spans, preserving element nodes
     (so inline <em>/<strong>/<a> formatting survives). */
  function splitTarget(target, mode, mask) {
    var units = [];
    var walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && node.nodeValue.replace(/\s+/g, '') !== '') textNodes.push(node);
    }
    textNodes.forEach(function (tn) {
      var frag = document.createDocumentFragment();
      var text = tn.nodeValue;
      if (mode === 'char') {
        for (var i = 0; i < text.length; i++) {
          var ch = text.charAt(i);
          if (ch === ' ') {
            frag.appendChild(document.createTextNode(' '));
          } else {
            var cu = document.createElement('span');
            cu.className = 'te-unit';
            cu.textContent = ch;
            units.push(cu);
            frag.appendChild(cu);
          }
        }
      } else {
        var parts = text.split(/(\s+)/);
        parts.forEach(function (p) {
          if (p === '') return;
          if (/^\s+$/.test(p)) {
            frag.appendChild(document.createTextNode(p));
            return;
          }
          var u = document.createElement('span');
          u.className = 'te-unit';
          u.textContent = p;
          if (mask) {
            var m = document.createElement('span');
            m.className = 'te-word-mask';
            m.appendChild(u);
            frag.appendChild(m);
          } else {
            frag.appendChild(u);
          }
          units.push(u);
        });
      }
      tn.parentNode.replaceChild(frag, tn);
    });
    return units;
  }

  function runSplit(wrap, target, effect) {
    var mode = effect === 'char-reveal' ? 'char' : 'word';
    var mask = effect === 'word-reveal';
    var units = splitTarget(target, mode, mask);
    if (!units.length) return;
    target.classList.add('te-effect-' + effect);
    var dur = num(wrap, 'data-te-duration', 800);
    var delay = num(wrap, 'data-te-delay', 0);
    var stagger = num(wrap, 'data-te-stagger', cfgStagger());
    target.style.setProperty('--te-duration', dur + 'ms');
    observe(wrap, function () {
      units.forEach(function (u, i) {
        u.style.transitionDelay = (delay + i * stagger) + 'ms';
        u.classList.add('te-in');
      });
    });
  }

  function runContinuous(wrap, target, cls) {
    var dur = num(wrap, 'data-te-duration', 800);
    target.style.setProperty('--te-duration', dur + 'ms');
    observe(wrap, function () { target.classList.add(cls); });
  }

  function runShiny(wrap, target) {
    var ink = getComputedStyle(target).color;
    if (ink) target.style.setProperty('--te-ink', ink);
    runContinuous(wrap, target, 'te-shiny-sweep');
  }

  function runTypewriter(wrap, target) {
    var full = target.textContent;
    if (!full) return;
    var stagger = num(wrap, 'data-te-stagger', cfgStagger()) || cfgStagger();
    var delay = num(wrap, 'data-te-delay', 0);
    target.setAttribute('aria-label', full);
    target.classList.add('te-typewriter');
    target.textContent = '';
    var textSpan = document.createElement('span');
    textSpan.className = 'te-tw-text';
    textSpan.setAttribute('aria-hidden', 'true');
    var caret = document.createElement('span');
    caret.className = 'te-caret';
    caret.setAttribute('aria-hidden', 'true');
    target.appendChild(textSpan);
    target.appendChild(caret);
    observe(wrap, function () {
      var i = 0;
      setTimeout(function step() {
        textSpan.textContent = full.slice(0, i);
        if (i < full.length) {
          i++;
          setTimeout(step, stagger);
        } else {
          target.classList.add('te-typed');
        }
      }, delay);
    });
  }

  /* ===== Shared count-up (window.NerdioCountUp) =====
     ONE easing/duration/format implementation for every numeric count-up in
     the theme: the te "count-up" effect below plus the counter and
     stat-figure blocks' inline bootstraps.
     opts: value (required), decimals, separator (thousands commas), prefix,
           suffix, duration (ms; 0 = instant), delay (ms).
     Motion-off (shared guard) or duration 0 → final value immediately. */
  function formatCount(v, o) {
    var decimals = Math.max(0, parseInt(o.decimals, 10) || 0);
    var out = Number(v).toFixed(decimals);
    if (o.separator) {
      var parts = out.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      out = parts.join('.');
    }
    return (o.prefix || '') + out + (o.suffix || '');
  }

  function countUp(el, opts) {
    var o = opts || {};
    var value = Number(o.value);
    if (!el || !Number.isFinite(value)) return;
    var duration = Math.max(0, Number(o.duration) || 0);
    var delay = Math.max(0, Number(o.delay) || 0);
    function finish() { el.textContent = formatCount(value, o); }
    if (duration === 0 || motionOff()) { finish(); return; }
    function run() {
      var start = performance.now();
      (function tick(now) {
        var p = Math.min(1, (now - start) / duration);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = formatCount(value * eased, o);
        if (p < 1) requestAnimationFrame(tick);
        else finish();
      })(start);
    }
    el.textContent = formatCount(0, o);
    if (delay) setTimeout(run, delay);
    else run();
  }
  countUp.format = function (value, opts) { return formatCount(value, opts || {}); };
  window.NerdioCountUp = countUp;

  function runCountUp(wrap, target) {
    var raw = target.textContent.trim();
    var m = raw.match(/^(\D*?)([\d.,]+)(\D*)$/);
    if (!m) return;
    var numStr = m[2];
    var opts = {
      value: parseFloat(numStr.replace(/,/g, '')),
      decimals: numStr.indexOf('.') !== -1 ? (numStr.split('.')[1] || '').length : 0,
      separator: numStr.indexOf(',') !== -1,
      prefix: m[1],
      suffix: m[3],
      duration: num(wrap, 'data-te-duration', 800),
      delay: num(wrap, 'data-te-delay', 0)
    };
    if (!Number.isFinite(opts.value)) return;
    target.classList.add('te-count-up');
    target.textContent = formatCount(0, opts);
    observe(wrap, function () { countUp(target, opts); });
  }

  function runRotating(wrap, target) {
    var raw = wrap.getAttribute('data-te-words') || '';
    var words = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!words.length) return;
    var dur = num(wrap, 'data-te-duration', 800);
    var hold = Math.max(dur * 2, 1600);
    target.classList.add('te-rotating');
    target.style.setProperty('--te-duration', dur + 'ms');
    target.textContent = '';
    var span = document.createElement('span');
    span.className = 'te-rot-word';
    span.textContent = words[0];
    target.appendChild(span);
    var idx = 0;
    observe(wrap, function () {
      // reveal the first word, then cycle
      requestAnimationFrame(function () { span.classList.add('te-in'); });
      var interval = setInterval(function () {
        span.classList.remove('te-in');
        setTimeout(function () {
          idx = (idx + 1) % words.length;
          span.textContent = words[idx];
          void span.offsetWidth;
          span.classList.add('te-in');
        }, dur);
      }, hold);
      wrap.__teCleanup = function () { clearInterval(interval); };
    });
  }

  function runScramble(wrap, target) {
    var full = target.textContent;
    if (!full) return;
    var dur = num(wrap, 'data-te-duration', 800);
    var delay = num(wrap, 'data-te-delay', 0);
    var pool = '!<>-_\\/[]{}=+*^?#abcdefghijklmnopqrstuvwxyz0123456789';
    target.setAttribute('aria-label', full);
    target.classList.add('te-scramble');
    target.textContent = '';
    var span = document.createElement('span');
    span.setAttribute('aria-hidden', 'true');
    span.textContent = full;
    target.appendChild(span);
    observe(wrap, function () {
      setTimeout(function () {
        var start = performance.now();
        (function frame(now) {
          var p = Math.min(1, (now - start) / dur);
          var revealed = Math.floor(p * full.length);
          var out = '';
          for (var i = 0; i < full.length; i++) {
            var c = full.charAt(i);
            if (i < revealed || c === ' ') out += c;
            else out += pool.charAt(Math.floor(Math.random() * pool.length));
          }
          span.textContent = out;
          if (p < 1) requestAnimationFrame(frame);
          else span.textContent = full;
        })(start);
      }, delay);
    });
  }

  function initEl(wrap) {
    if (wrap.__teInit) return;
    wrap.__teInit = true;
    var effect = wrap.getAttribute('data-te-effect');
    if (!effect || effect === 'none') return;
    var target = resolveTarget(wrap);
    if (!target) return;
    if (motionOff()) return; // leave the final text as-is, no motion

    /* Snapshot the pristine target before any split/rewrite so destroy() can
       restore it — otherwise a section re-render re-splits already-split
       markup and nests .te-unit spans. */
    wrap.__teTarget = target;
    if (typeof target.__teOriginal !== 'string') {
      target.__teOriginal = target.innerHTML;
      target.__teClass = target.className;
      target.__teStyle = target.getAttribute('style');
      target.__teAria = target.getAttribute('aria-label');
    }

    switch (effect) {
      case 'word-reveal':
      case 'char-reveal':
      case 'fade-up-stagger':
      case 'blur-in':
        runSplit(wrap, target, effect);
        break;
      case 'gradient-animate':
        runContinuous(wrap, target, 'te-gradient-animate');
        break;
      case 'shiny-sweep':
        runShiny(wrap, target);
        break;
      case 'typewriter':
        runTypewriter(wrap, target);
        break;
      case 'count-up':
        runCountUp(wrap, target);
        break;
      case 'rotating-words':
        runRotating(wrap, target);
        break;
      case 'scramble':
        runScramble(wrap, target);
        break;
    }
  }

  function boot(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    if (root.matches && root.matches('[data-te-effect]')) initEl(root);
    root.querySelectorAll('[data-te-effect]').forEach(initEl);
  }

  function destroy(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = Array.from(root.querySelectorAll('[data-te-effect]'));
    if (root.matches && root.matches('[data-te-effect]')) nodes.unshift(root);
    nodes.forEach(function (wrap) {
      if (wrap.__teCleanup) wrap.__teCleanup();
      delete wrap.__teCleanup;
      var target = wrap.__teTarget;
      if (target && typeof target.__teOriginal === 'string') {
        /* Restore the pre-split state so a later init() starts clean. */
        target.innerHTML = target.__teOriginal;
        target.className = target.__teClass || '';
        if (target.__teStyle == null) target.removeAttribute('style');
        else target.setAttribute('style', target.__teStyle);
        if (target.__teAria == null) target.removeAttribute('aria-label');
        else target.setAttribute('aria-label', target.__teAria);
        delete target.__teOriginal;
        delete target.__teClass;
        delete target.__teStyle;
        delete target.__teAria;
      }
      delete wrap.__teTarget;
      delete wrap.__teInit;
    });
  }

  window.NerdioTextEffects = { init: boot, destroy: destroy };
})();
